import { useSessionStorage, tryOnMounted } from '@vueuse/core'

import { EdbCategoryID } from '@/types/api'
import type { BranchTagKind, BranchTagLink } from '@/types/branch'

import {
  GtmEventMap,
  SearchKeywordSource,
  SearchEventSource,
  SearchUIComponentLabel,
  BranchInfoUIComponent,
  BranchInfoUIElementType,
} from '@/view-models/gtm'
import { SITE_ID } from '@/constants/storageKey'

export function useGtm() {
  const { $gtm, $store: store } = useNuxtApp() as any

  const loginEventSuffix = ' (in login state)'

  function sendGtmEvent(eventData: GtmEventMap | Record<string, unknown>, isLogin = false) {
    let eventPayload = typeof eventData === 'string' ? { event: eventData } : eventData
    eventPayload = {
      ...eventPayload,
      event: isLogin ? `${eventPayload.event}${loginEventSuffix}` : eventPayload.event,
      store: store.state,
    }

    $gtm.push({ store: null, payload: null } as any)
    $gtm.push(eventPayload as any)
  }

  return {
    sendGtmEvent,
  }
}

interface FireSearchQuerySentOptions {
  keyword: string
  isEnter?: boolean
  sourceName?: string
  isListPage?: boolean
  isEmbeddedSearchBarVisible?: boolean
}

export function useGtmSearchQuerySent() {
  const { sendGtmEvent } = useGtm()
  const route = useRoute()

  const keywordSource = ref<SearchKeywordSource | ''>('')

  const routeName = computed(() => route.name?.toString() || '')
  const isRegionCategoriesPage = computed(() => checkIfRegionCategoriesPage(routeName.value))
  const isRegionThemesPage = computed(() => checkIfRegionThemesPage(routeName.value))
  const isRegionHomePage = computed(() => checkIfRegionHomePage(routeName.value))

  function getSource(): SearchEventSource | null {
    // PM: 限時促銷分類 (10782) 獨立為 FLASH_SALES_LIST source，其餘 category/theme 走 CATEGORY_LIST
    if (isRegionCategoriesPage.value && Number(route.params.id) === EdbCategoryID.CategoryIDFlashSale) {
      return SearchEventSource.FLASH_SALES_LIST
    }
    if (isRegionCategoriesPage.value || isRegionThemesPage.value) {
      return SearchEventSource.CATEGORY_LIST
    }
    if (isRegionHomePage.value) {
      return SearchEventSource.HOME
    }
    return null
  }

  function getSourceId(): string {
    if (isRegionCategoriesPage.value || isRegionThemesPage.value) {
      return `${route.params.id || ''}`
    }
    return ''
  }

  function getUIComponentLabel({
    isListPage,
    isEmbeddedSearchBarVisible,
  }: Pick<FireSearchQuerySentOptions, 'isListPage' | 'isEmbeddedSearchBarVisible'>): SearchUIComponentLabel | null {
    if (!isListPage) {
      return null
    }
    return isEmbeddedSearchBarVisible ? SearchUIComponentLabel.MAIN : SearchUIComponentLabel.STICKY
  }

  function buildEventData(options: FireSearchQuerySentOptions): Record<string, unknown> {
    const { keyword, isEnter = false, sourceName = '', isListPage, isEmbeddedSearchBarVisible } = options
    const source = getSource()
    const sourceId = getSourceId()
    const uiComponentLabel = getUIComponentLabel({ isListPage, isEmbeddedSearchBarVisible })

    return {
      event: GtmEventMap.SEARCH_QUERY_SENT,
      payload: {
        keyword,
        hotKeyword: keywordSource.value === SearchKeywordSource.POPULAR,
        searchHistory: keywordSource.value === SearchKeywordSource.HISTORY,
        enter: isEnter,
        ...(source && { source }),
        ...(sourceId && { sourceId }),
        ...(sourceName && { sourceName }),
        ...(uiComponentLabel && { uiComponent: uiComponentLabel }),
      },
    }
  }

  function fireSearchQuerySentEvent(options: FireSearchQuerySentOptions) {
    // PM: 清除搜尋框後的搜尋不送事件
    if (!options.keyword) {
      return
    }

    sendGtmEvent(buildEventData(options))
    keywordSource.value = ''
  }

  return { keywordSource, fireSearchQuerySentEvent }
}

const KIND_TO_ELEMENT_TYPE: Record<BranchTagKind, BranchInfoUIElementType> = {
  region: BranchInfoUIElementType.REGION_DESCRIPTION,
  mainCuisine: BranchInfoUIElementType.MAIN_DISHES,
  industry: BranchInfoUIElementType.INDUSTRY_TYPE,
}

export function useGtmBranchInfoClicked() {
  const { sendGtmEvent } = useGtm()

  function send(uiComponent: BranchInfoUIComponent, uiElementType: BranchInfoUIElementType, uiElementName: string) {
    sendGtmEvent({
      event: GtmEventMap.BRANCH_INFO_CLICKED,
      payload: { uiComponent, uiElementType, uiElementName },
    })
  }

  function fireOpeningHoursClicked(uiElementName: string) {
    send(BranchInfoUIComponent.INFO, BranchInfoUIElementType.BUSINESS_HOUR, uiElementName)
  }

  function fireTagLinkClicked(link: BranchTagLink) {
    send(BranchInfoUIComponent.LABEL, KIND_TO_ELEMENT_TYPE[link.kind], link.tag.name ?? '')
  }

  return { fireOpeningHoursClicked, fireTagLinkClicked }
}

export function useIdentifyAndAttribution(nuxtApp = useNuxtApp()) {
  const route = useRoute()
  const { sendGtmEvent } = useGtm()
  const siteIdSession = useSessionStorage(SITE_ID, '')
  const { $store: store } = nuxtApp
  const userInfo = computed(() => store.getters['authentication/getUser'])

  tryOnMounted(() => {
    const siteId = route.query.site_id as string
    if (!siteId) {
      return
    }
    siteIdSession.value = siteId
  })

  watch(userInfo, (newUserInfo, oldUserInfo) => {
    // identify
    if (!oldUserInfo && newUserInfo) {
      sendGtmEvent({
        event: GtmEventMap.IDENTIFY,
        payload: {
          uid: newUserInfo.uid,
        },
      })
    }
    if (oldUserInfo && !newUserInfo) {
      sendGtmEvent(GtmEventMap.ACCOUNT_LOGGED_OUT)
    }

    // attribution
    if (!oldUserInfo && newUserInfo && siteIdSession.value) {
      sendGtmEvent({
        event: GtmEventMap.SMART_LINK_TRIGGERED,
        payload: {
          site_id: siteIdSession.value,
        },
      })
    }
  })
}
